import React, { Fragment } from 'react';
import PropTypes from 'prop-types';

import { trans } from '@spotahome/soyuz/client';

import {
  formatReadableDateRange,
  formatReadableDateWithRentalType
} from '@spotahome/marketplace-common/src/utils/DateRangeHandler';

import {
  useHotspotItem,
  HOTSPOT_FILTERS
} from '../../../context/HotspotsContext';

import DropdownButton from '../../DropdownButton';
import AvailabilityFilter from '../../AvailabilityFilter';

import FiltersExposedTitle from './FiltersExposedTitle';
import FiltersExposedButton from './FiltersExposedButton';
import FiltersExposedInfoBox from './FiltersExposedInfoBox';

const AvailabilitiesFilter = ({
  moveIn,
  moveOut,
  selectedRentalTypes,
  isFilterOpen,
  onToggleFilter,
  onFiltersUpdate,
  minimumStaying,
  totalResults,
  moveOutFrom,
  moveOutTo,
  moveInFrom,
  moveInTo,
  className
}) => {
  const { setHotspotVisited, renderHotspotLed } = useHotspotItem(
    HOTSPOT_FILTERS.availabilities
  );

  const renderAvailabilitiesButtonContent = () => {
    const formattedLabel =
      moveOut || (moveOutFrom && moveOutTo)
        ? formatReadableDateRange(moveIn || moveInFrom, moveOut || moveOutTo)
        : formatReadableDateWithRentalType(
            moveIn || moveInFrom,
            selectedRentalTypes
          );

    return formattedLabel || trans('filters.label.availabilities');
  };

  const handleClose = () => {
    setHotspotVisited();
    onToggleFilter('date');
  };

  return (
    <DropdownButton
      isOpen={isFilterOpen}
      isSelected={
        !!(moveIn || moveOut) ||
        !!selectedRentalTypes.length ||
        !!(moveInFrom || moveInTo)
      }
      onToggle={() => onToggleFilter('date')}
      onClose={handleClose}
      className={className}
      dataTest="date-exposed-filter-button"
      renderButtonContent={() => (
        <Fragment>
          {renderAvailabilitiesButtonContent()}
          {renderHotspotLed()}
        </Fragment>
      )}
      renderDropdownContent={handleDropdownClose => (
        <>
          <div className="filters-exposed__content">
            <FiltersExposedTitle
              title={trans('search.exposedfilters.dates.title')}
              subtitle={trans('search.exposedfilters.dates.subtitle')}
            />
            <FiltersExposedInfoBox
              filterName={HOTSPOT_FILTERS.availabilities}
            />
            <AvailabilityFilter
              from={moveIn}
              minimumStaying={minimumStaying}
              to={moveOut}
              rentalType={selectedRentalTypes}
              updateFiltersWith={onFiltersUpdate}
              moveOutFrom={moveOutFrom}
              moveOutTo={moveOutTo}
              moveInFrom={moveInFrom}
              moveInTo={moveInTo}
            />
          </div>
          <FiltersExposedButton
            onClick={handleDropdownClose}
            text={trans('search.filters.view_close_filters', {
              nResults: `${totalResults}`
            })}
            dataTest="date-exposed-filter-apply"
            eventLabel="apply-date"
          />
        </>
      )}
    />
  );
};

AvailabilitiesFilter.propTypes = {
  moveIn: PropTypes.string.isRequired,
  moveOut: PropTypes.string.isRequired,
  selectedRentalTypes: PropTypes.arrayOf(PropTypes.string).isRequired,
  isFilterOpen: PropTypes.bool.isRequired,
  onToggleFilter: PropTypes.func.isRequired,
  onFiltersUpdate: PropTypes.func.isRequired,
  minimumStaying: PropTypes.number.isRequired,
  totalResults: PropTypes.number.isRequired,
  moveOutFrom: PropTypes.string,
  moveOutTo: PropTypes.string,
  moveInFrom: PropTypes.string,
  moveInTo: PropTypes.string,
  className: PropTypes.string
};

AvailabilitiesFilter.defaultProps = {
  moveOutFrom: '',
  moveOutTo: '',
  moveInFrom: '',
  moveInTo: '',
  className: ''
};

export default AvailabilitiesFilter;
